import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { getGame, setGame } from './actions';
import { selectActiveGame, selectOpponent, selectPlayer } from './selectors';
import { BoardArea, Panel, Screen, Top, TopRow, Viewport } from './styled';
import { ViewOption, ViewSwitch } from '../Controls';
import MatBoard from '../Board/MatBoard';
import TableBoard from '../Board/TableBoard';
import { MAT_VIEW, TABLE_VIEW, useBoardView } from '../Board/useBoardView';
import { isMatTurned } from '../Board/layout';
import Card from '../Card';
import Hand from '../Hand';
import PlayerBar from '../PlayerBar';
import TurnBanner from '../TurnBanner';
import DeckLook from '../Trainer/DeckLook';
import FlyingCards from '../Motion/FlyingCards';
import ActivePokemonMenu from '../ActivePokemon/ActivePokemonMenu';
import BenchContextMenu from '../Bench/BenchContextMenu';
import DeckContextMenu from '../Deck/DeckContextMenu';
import DiscardPileContextMenu from '../DiscardPile/DiscardPileContextMenu';
import RetreatContextMenu from '../RetreatButton/RetreatContextMenu';
import { Muted } from '../Controls';
import GameLog, { InlineLog, OpenLog } from '../GameLog';
import useWideScreen from './useWideScreen';
import CoinToss from '../Coins/CoinToss';
import DropTarget from '../Drag/DropTarget';
import { BOARD_DROP, useDragging } from '../Drag/dropTargets';

const Game = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const game = useSelector(selectActiveGame);
  const player = useSelector(selectPlayer);
  const opponent = useSelector(selectOpponent);
  const [view, setView] = useBoardView();
  const wide = useWideScreen();
  const dragging = useDragging();

  useEffect(() => {
    if (!game) {
      dispatch(setGame(null));
      dispatch(getGame(id));
    }
  }, [dispatch, id, game]);

  if (!game) {
    return <Viewport><Muted style={{ padding: 16 }}>Loading game…</Muted></Viewport>;
  }

  const playerId = player.getIn(['user', 'id']);
  const opponentId = opponent.getIn(['user', 'id']);
  const turned = isMatTurned(game, playerId);
  const userIds = [playerId, opponentId];

  return (
    <Viewport>
      <Screen>
        {/* A Trainer that goes on no Pokémon is played by dropping it anywhere on the board. */}
        <BoardArea data-anchor={BOARD_DROP}>
          {dragging && dragging.dropAnywhere && <DropTarget name={BOARD_DROP} commit={dragging.dropAnywhere} />}
          {view === MAT_VIEW ?
            <MatBoard playerId={playerId} opponentId={opponentId} turned={turned} /> :
            <TableBoard playerId={playerId} opponentId={opponentId} />}
        </BoardArea>

        <Top>
          <TopRow>
            <Link to="/games">‹ Games</Link>
            {!wide && <OpenLog userId={playerId} />}
            <ViewSwitch role="group" aria-label="Board view">
              <ViewOption $on={view === MAT_VIEW} onClick={() => setView(MAT_VIEW)}>Mat</ViewOption>
              <ViewOption $on={view === TABLE_VIEW} onClick={() => setView(TABLE_VIEW)}>Table</ViewOption>
            </ViewSwitch>
          </TopRow>
          <PlayerBar userId={opponentId} />
        </Top>

        <Panel>
          <TurnBanner userId={playerId} />
          <PlayerBar userId={playerId} you />
          <Hand userId={playerId} />
          {wide && <InlineLog userId={playerId} />}
        </Panel>
      </Screen>
      <DeckLook userId={playerId} />
      <FlyingCards />
      <CoinToss />

      {userIds.map(userId => (
        <React.Fragment key={userId}>
          <ActivePokemonMenu userId={userId} />
          <BenchContextMenu userId={userId} />
          <DeckContextMenu userId={userId} />
          <DiscardPileContextMenu userId={userId} />
        </React.Fragment>
      ))}
      <RetreatContextMenu userId={playerId} />
      <GameLog userId={playerId} />
      <Card />
    </Viewport>
  );
};

export default Game;
